/**
 * HustleRoundupHistoryPage — list of past & current meetings for the user.
 * GET /api/hustleroundup/history/mine
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { History, Video, ArrowLeft, Sparkles, PlayCircle, Circle } from "lucide-react";
import { apiFetch, fetchMe } from "@/lib/api";

export default function HustleRoundupHistoryPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    (async () => {
      const u = await fetchMe();
      if (!u?.id) { nav("/login?next=/history"); return; }
      try {
        const list = await apiFetch("/hustleroundup/history/mine");
        setRows(list || []);
      } catch (e) { setErr(e.message || "Failed to load"); }
    })();
  }, [nav]);

  return (
    <div data-testid="hustleroundup-history"
         className="min-h-screen bg-stone-950 text-white px-6 py-10">
      <div className="max-w-4xl mx-auto">
        <button
          data-testid="history-back"
          onClick={() => nav("/")}
          className="text-stone-400 hover:text-white text-sm flex items-center gap-2 mb-8"
        ><ArrowLeft className="w-4 h-4" /> Back</button>

        <div className="flex items-center gap-3 mb-8">
          <History className="w-7 h-7 text-emerald-400" />
          <h1 className="text-3xl font-bold">Meeting history</h1>
        </div>

        {err && <div className="text-sm text-red-400 mb-4">{err}</div>}
        {!rows && !err && <div className="text-stone-500 text-sm animate-pulse">Loading…</div>}
        {rows && rows.length === 0 && (
          <div data-testid="history-empty"
               className="p-8 rounded-2xl border border-dashed border-stone-800 text-center text-stone-500 text-sm">
            No meetings yet. <Link to="/meet" className="text-emerald-400 underline">Start one</Link>.
          </div>
        )}

        <ul data-testid="hustleroundup-history-list" className="space-y-2">
          {(rows || []).map((m) => (
            <li key={m.id} data-testid="hustleroundup-history-row"
                className="flex items-center gap-4 p-4 rounded-xl border border-stone-900 bg-stone-900/40 hover:border-stone-700">
              <Video className="w-5 h-5 text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{m.room_name || m.livekit_room}</div>
                <div className="text-xs text-stone-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                  <span>Started {new Date(m.started_at).toLocaleString()}</span>
                  {m.duration_s && <span>· {Math.round(m.duration_s / 60)} min</span>}
                  {m.participant_count_peak > 0 && <span>· peak {m.participant_count_peak}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                {m.recording_state && (
                  <span data-testid={`row-rec-${m.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${badge(m.recording_state)}`}>
                    <PlayCircle className="w-3 h-3" /> {m.recording_state}
                  </span>
                )}
                {m.transcript_state && (
                  <span data-testid={`row-trans-${m.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${badge(m.transcript_state)}`}>
                    <Sparkles className="w-3 h-3" /> {m.transcript_state}
                  </span>
                )}
                {!m.ended_at && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300">
                    <Circle className="w-2 h-2 fill-current" /> live
                  </span>
                )}
                {m.recording_id && (
                  <Link to={`/recording/${m.recording_id}`}
                        data-testid={`row-open-${m.id}`}
                        className="ml-1 px-3 py-1 rounded-full bg-emerald-500 text-stone-950 font-semibold">
                    Open
                  </Link>
                )}
                {!m.recording_id && m.room_slug && (
                  <Link to={`/meet/${m.room_slug}`}
                        className="ml-1 px-3 py-1 rounded-full border border-stone-700">
                    Rejoin
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function badge(state) {
  const map = {
    ready: "bg-emerald-500/15 text-emerald-300",
    active: "bg-emerald-500/15 text-emerald-300",
    starting: "bg-amber-500/15 text-amber-300",
    uploading: "bg-amber-500/15 text-amber-300",
    transcribing: "bg-amber-500/15 text-amber-300",
    summarising: "bg-amber-500/15 text-amber-300",
    queued: "bg-stone-700/40 text-stone-300",
    failed: "bg-red-500/15 text-red-300",
  };
  return map[state] || "bg-stone-800 text-stone-300";
}
